import { memo, useMemo } from 'react'

import { BottomSheet } from '~/shared/ui/BottomSheet'
import { Button } from '~/shared/ui/Button'
import { Text } from '~/shared/ui/Text'
import { WheelPicker, WheelPickerGroup } from '~/shared/ui/WheelPicker'

import {
  DATE_WHEEL_HEIGHT_PX,
  DAY_WHEEL_FLEX,
  DEFAULT_MIN_DATE,
  MONTH_WHEEL_FLEX,
  YEAR_WHEEL_FLEX,
  buildDayOptions,
  buildMonthOptions,
  buildYearOptions,
} from './config'
import { useDatePickerDraft } from './hooks'
import { resolveDayBounds, resolveMonthBounds } from './utils'
import type { DatePickerSheetProps } from './types'

/**
 * Шторка выбора даты: три барабана (день/месяц/год) поверх BottomSheet + «Готово».
 * Черновой выбор живёт локально, onChange вызывается только по кнопке (спека 02 §A2·2).
 */
export const DatePickerSheet = memo(function DatePickerSheet(props: DatePickerSheetProps) {
  const {
    isOpen,
    onClose,
    title,
    value,
    defaultValue,
    minDate = DEFAULT_MIN_DATE,
    onChange,
  } = props

  // `new Date()` в значении по умолчанию давало новый объект на каждый рендер — и обрезка
  // барабанов пересчитывалась вхолостую. Дефолт считаем один раз на жизнь компонента.
  const maxDate = useMemo(() => props.maxDate ?? new Date(), [props.maxDate])

  const minYear = minDate.getFullYear()
  const maxYear = maxDate.getFullYear()

  const { draft, setDay, setMonth, setYear } = useDatePickerDraft(
    isOpen,
    value,
    defaultValue,
    minDate,
    maxDate,
  )

  // Барабаны обрезаны границами целиком, а не только по году: иначе в крайнем году остаются
  // все двенадцать месяцев, и «сегодня» или «младше 14» выбирается пальцем в два движения.
  const monthOptions = useMemo(() => {
    const bounds = resolveMonthBounds(draft.year, minDate, maxDate)
    return buildMonthOptions(bounds.from, bounds.to)
  }, [draft.year, minDate, maxDate])

  const dayOptions = useMemo(() => {
    const bounds = resolveDayBounds(draft.year, draft.month, minDate, maxDate)
    return buildDayOptions(bounds.from, bounds.to)
  }, [draft.year, draft.month, minDate, maxDate])

  const yearOptions = useMemo(() => buildYearOptions(minYear, maxYear), [minYear, maxYear])

  const handleConfirm = () => {
    onChange(new Date(draft.year, draft.month, draft.day))
    onClose()
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <BottomSheet.Header>
        <Text variant='H2' as='h2'>
          {title}
        </Text>
      </BottomSheet.Header>
      <BottomSheet.Content>
        <WheelPickerGroup flexes={[DAY_WHEEL_FLEX, MONTH_WHEEL_FLEX, YEAR_WHEEL_FLEX]}>
          <WheelPicker
            options={dayOptions}
            value={String(draft.day)}
            onChange={(next) => setDay(Number(next))}
          />
          <WheelPicker
            options={monthOptions}
            value={String(draft.month)}
            onChange={(next) => setMonth(Number(next))}
          />
          <WheelPicker
            options={yearOptions}
            value={String(draft.year)}
            onChange={(next) => setYear(Number(next))}
          />
        </WheelPickerGroup>
      </BottomSheet.Content>
      <BottomSheet.Footer>
        <Button variant='PRIMARY' width='FULL' text='Готово' onPress={handleConfirm} />
      </BottomSheet.Footer>
    </BottomSheet>
  )
})
